<!--
/**
 * 用于逐步替代 Product 组件 - NewProduct
 *
 * 功能描述：
 * 1. 替代 Product 组件，使 其相关 数据功能都从外部获取
 *
 * Props
 * productList {Array} [[]] - 产品列表
 * loading {boolean} [false] - 是否处于 loading 请求
 * error {string} [null] - 错误信息
 *
 * 使用示例:
 * <Product :product-list="list" :loading="loading" :error="error" />
 *
 */
-->
<template>
  <div>
    <!-- 商品列表 -->
    <div
      class="product-list"
      v-if="productList.length || loading"
    >
      <template
        v-for="(item, index) in productList"
        :key="index"
      >
        <template v-if="item.type === 'ai-search-spot'">
          <H5AiSearchSpot
            :item="item"
            v-if="deviceType === 'MOBILE'"
          />
          <AiSearchSpot
            :item="item"
            v-else
          />
        </template>
        <div
          v-else
          class="product-item-block"
          :data-id="item.offerId"
        >
          <ProductItem
            :item="item"
            @on-click="(data) => emit('itemClick', data)"
          />
        </div>
      </template>
      <!-- 加载更多骨架屏 -->
      <LoadMoreSkeleton
        v-if="loading"
        :length="productList.length"
      />
    </div>
    <!-- 搜索结果为空 -->
    <Empty
      v-else
      :msg="error || 'Sorry, no results found'"
    />
  </div>
</template>

<script setup>
import { buriedPointUtil, throttle } from '@/utils/utils'
import { ProductItem } from '@/components/ProductItem'
import Empty from '@/components/Empty/index.vue'
import LoadMoreSkeleton from './components/LoadMoreSkeleton/index.vue'
import AiSearchSpot from './components/AiSearchSpot.vue'
import H5AiSearchSpot from './components/H5AiSearchSpot.vue'
import { useDevice } from '@/hooks/useDevice'

const props = defineProps({
  productList: {
    type: Array,
    required: true
  },
  loading: Boolean,
  error: {
    type: String,
    default: null
  }
})
const emit = defineEmits(['load', 'itemClick'])

const { deviceType } = useDevice()

const inViewImages = ref(new Set())

const lastPageSize = ref(0)
watch(() => props.productList, (value, oldValue) => {
  if (value.length) {
    lastPageSize.value = value.length - oldValue.length
    nextTick(() => {
      setTimeout(() => {
        observerProduct()
      }, 100)
    })
  } else {
    // 若列表为空则重置状态
    observerIdList.value = new Set()
  }
})

let observer2
const observerIdList = ref(new Set()) // 正在观察的 offerId 列表
const reportedOfferIdList = ref([]) // 已报告的 offerId 列表

const observerProduct = () => {
  if (observer2) {
    observer2.disconnect() // 先断开观察，防止重复监听
  }
  observer2 = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const offerId = entry.target.dataset.id
        observerIdList.value.add(offerId)
        reportExposure()
        preSearch()
      }
    })
  }, { threshold: 0.51 })

  document.querySelectorAll('.product-item-block').forEach(product => {
    observer2.observe(product)
  })
}

// 埋点上报
const reportExposure = throttle(() => {
  const offerIdList = []
  observerIdList.value.forEach(id => {
    if (reportedOfferIdList.value.indexOf(id) === -1) {
      offerIdList.push(id)
      reportedOfferIdList.value.push(id) // 添加到已报告的列表中
    }
  })

  if (offerIdList.length > 0) {
    if (deviceType.value === 'MOBILE') {
      buriedPointUtil({ eventId: 'MPEX101', others: { offerIdList } })
    } else {
      buriedPointUtil({ eventId: 'PEX101', others: { offerIdList } })
    }
  }
}, 500)

/**
 * 预加载, 使用 isPreSearch 字段是为了防止 preSearch 同时触发多次导致 load 异常
 */
const isPreSearch = ref(false)

watch(isPreSearch, (val) => {
  if (val) {
    emit('load')
    isPreSearch.value = false
  }
})

const preSearch = () => {
  const reportedCount = observerIdList.value.size // 已曝光商品总数

  const half = Math.floor(lastPageSize.value / 2)
  const total = new Set(props.productList.map(item => item.offerId)).size
  const point = total - half
  if (reportedCount >= point) {
    isPreSearch.value = true
  }
}

// 当图片进入视口时标记该图片
const handleImageIntersection = (entries, observer) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      const imageId = entry.target.dataset.id
      inViewImages.value.add(imageId) // 标记图片已加载
      observer.unobserve(entry.target) // 只触发一次，停止观察
    }
  })
}

onMounted(() => {
  const observer = new IntersectionObserver(handleImageIntersection, {
    rootMargin: '0px 0px 200px 0px', // 图片提前 200px 开始加载
    threshold: 0.1 // 只有当图片至少 10% 显示时才触发
  })

  // 使用 nextTick 确保组件渲染完成后再进行图片懒加载观察
  nextTick(() => {
    const images = document.querySelectorAll('img') // 获取所有图片
    images.forEach((image) => observer.observe(image)) // 开始观察每个图片
  })

  observer2 = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const offerId = entry.target.dataset.id
        observerIdList.value.add(offerId)
        reportExposure()
        preSearch()
      }
    })
  }, { threshold: 0.51 })
})
</script>

<style lang="less" scoped>
.product-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px 13px;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 20px 16px;
  }
}
</style>
